'use client'

import { Currency, formatNumber } from '@/utils/loan-calculator'
import {
  motion,
  useInView,
  useMotionValue,
  useSpring,
  useTransform,
} from 'motion/react'
import { useEffect, useRef } from 'react'

export function AnimatedCurrency({
  start,
  end,
  decimals = 0,
  currency = 'HUF',
}: {
  start: number
  end: number
  decimals?: number
  currency?: Currency
}) {
  let ref = useRef(null)
  let isInView = useInView(ref, { once: true, amount: 0.8 })

  let value = useMotionValue(start)
  let spring = useSpring(value, { damping: 30, stiffness: 300 })
  let display = useTransform(spring, (num) => formatNumber(currency, num))
  // let display = useTransform(spring, (num) => num.toFixed(decimals))

  useEffect(() => {
    if (isInView) {
      // Always animate to the latest end value when it changes
      value.set(end)
    } else {
      value.set(start)
    }

    // Return cleanup function to reset when unmounting
    return () => {
      spring.stop()
    }
  }, [start, end, isInView, value, spring])

  return <motion.span ref={ref}>{display}</motion.span>
}

export function AnimatedNumber({
  start,
  end,
  decimals = 0,
}: {
  start: number
  end: number
  decimals?: number
}) {
  let ref = useRef(null)
  let isInView = useInView(ref, { once: true, amount: 0.8 })

  let value = useMotionValue(start)
  let spring = useSpring(value, { damping: 30, stiffness: 300 })
  let display = useTransform(spring, (num) => num.toFixed(decimals))

  useEffect(() => {
    if (isInView) {
      // Always animate to the latest end value when it changes
      value.set(end)
    } else {
      value.set(start)
    }

    // Return cleanup function to reset when unmounting
    return () => {
      spring.stop()
    }
  }, [start, end, isInView, value, spring])

  return <motion.span ref={ref}>{display}</motion.span>
}

export function CachableAnimatedNumber({
  start,
  end,
  decimals = 0,
}: {
  start: number
  end: number
  decimals?: number
}) {
  let ref = useRef(null)
  let isInView = useInView(ref, { once: true, amount: 0.5 })

  let value = useMotionValue(start)
  let spring = useSpring(value, { damping: 30, stiffness: 100 })
  let display = useTransform(spring, (num) => num.toFixed(decimals))

  useEffect(() => {
    value.set(isInView ? end : start)
  }, [start, end, isInView, value])

  return <motion.span ref={ref}>{display}</motion.span>
}
